export type TransferSpeed = "fast" | "standard";

/** Circle attests at or below 1000 as a Fast Transfer and at or above 2000 as Standard. */
export const MIN_FINALITY_THRESHOLD: Record<TransferSpeed, number> = {
  fast: 1000,
  standard: 2000,
};

export interface TransferFeeQuote {
  /** Fee at the quoted rate, in USDC subunits. */
  expectedFee: bigint;
  /** `maxFee` for `depositForBurn`: the expected fee plus headroom for a rate change. */
  maxFee: bigint;
}

// Circle's guidance. Below the live rate, a Fast Transfer silently settles as Standard;
// anything above the charged fee is not taken.
const MAX_FEE_BUFFER_PERCENT = BigInt(20);

// Circle quotes rates like 1.3 and 1.4 bps; hundredths of a bp keep that exact in integers.
const RATE_SCALE = 100;
const SUBUNITS_PER_SCALED_BP = BigInt(1_000_000);

const ceilDiv = (a: bigint, b: bigint) => (a + b - BigInt(1)) / b;

/**
 * Quotes a transfer from Circle's `minimumFee` (basis points) for the route.
 * Returns null when there is no valid quote, including when the fee would reach the amount,
 * which Circle's burn rejects.
 */
export function quoteTransferFee(amount: bigint, minimumFeeBps: number): TransferFeeQuote | null {
  if (amount <= BigInt(0) || !Number.isFinite(minimumFeeBps) || minimumFeeBps < 0) return null;

  const scaledRate = BigInt(Math.round(minimumFeeBps * RATE_SCALE));
  const expectedFee = ceilDiv(amount * scaledRate, SUBUNITS_PER_SCALED_BP);
  const maxFee = ceilDiv(expectedFee * (BigInt(100) + MAX_FEE_BUFFER_PERCENT), BigInt(100));

  if (maxFee >= amount) return null;
  return { expectedFee, maxFee };
}
